import { MessageSquare, Building2, FileSearch, BookOpen, Compass, FileEdit } from 'lucide-react';

interface Choice {
  id: string;
  icon: typeof MessageSquare;
  title: string;
  description: string;
  color: string;
}

const CHOICES: Choice[] = [
  {
    id: 'chat',
    icon: MessageSquare,
    title: 'Chat with Documents',
    description: 'Analyze patents, applications, or publications with AI assistance',
    color: 'blue'
  },
  {
    id: 'uspto',
    icon: Building2,
    title: 'View USPTO Data',
    description: 'Check prosecution history and application status',
    color: 'indigo'
  },
  {
    id: 'office_action',
    icon: FileSearch,
    title: 'Examine an Office Action',
    description: 'Review rejections and plan your response strategy',
    color: 'purple'
  },
  {
    id: 'drafts',
    icon: FileEdit,
    title: 'Draft a Document with AI',
    description: 'Create patent specs, office action responses, or summaries with AI assistance',
    color: 'pink'
  },
  {
    id: 'laws',
    icon: BookOpen,
    title: 'Look Up Laws & Guidance',
    description: 'Search MPEP, 37 CFR, and 35 USC regulations',
    color: 'teal'
  },
  {
    id: 'browse_tasks',
    icon: FileSearch,
    title: 'Browse All Tasks',
    description: 'Explore all 21 available tasks organized by category',
    color: 'green'
  },
  {
    id: 'tour',
    icon: Compass,
    title: 'General App Info.',
    description: 'Get a complete overview of all features',
    color: 'amber'
  }
];

interface ChoiceStepProps {
  onSelectChoice: (choiceId: string) => void;
  selectedChoice?: string;
}

export function ChoiceStep({ onSelectChoice, selectedChoice }: ChoiceStepProps) {
  const getColorClasses = (color: string, isSelected: boolean) => {
    const colorMap: Record<string, { bg: string; border: string; icon: string; selectedBg: string; selectedBorder: string }> = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        selectedBg: 'bg-blue-100',
        selectedBorder: 'border-blue-500'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        icon: 'text-indigo-600',
        selectedBg: 'bg-indigo-100',
        selectedBorder: 'border-indigo-500'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: 'text-purple-600',
        selectedBg: 'bg-purple-100',
        selectedBorder: 'border-purple-500'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        icon: 'text-teal-600',
        selectedBg: 'bg-teal-100',
        selectedBorder: 'border-teal-500'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        icon: 'text-amber-600',
        selectedBg: 'bg-amber-100',
        selectedBorder: 'border-amber-500'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        selectedBg: 'bg-green-100',
        selectedBorder: 'border-green-500'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        icon: 'text-pink-600',
        selectedBg: 'bg-pink-100',
        selectedBorder: 'border-pink-500'
      }
    };
    const colors = colorMap[color] || colorMap.blue;
    return {
      bg: isSelected ? colors.selectedBg : colors.bg,
      border: isSelected ? colors.selectedBorder : colors.border,
      icon: colors.icon
    };
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-gray-900">What would you like to do today?</h2>
        <p className="text-gray-600">
          Choose a task to get started, and we'll guide you through it
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {CHOICES.map((choice) => {
          const Icon = choice.icon;
          const isSelected = selectedChoice === choice.id;
          const colors = getColorClasses(choice.color, isSelected);

          return (
            <button
              key={choice.id}
              onClick={() => onSelectChoice(choice.id)}
              className={`text-left p-5 border-2 rounded-lg transition-all hover:shadow-md ${colors.bg} ${colors.border}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    {choice.title}
                    {isSelected && (
                      <span className="text-xs bg-white px-2 py-0.5 rounded-full font-medium">
                        Selected
                      </span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-600">{choice.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="text-center text-sm text-gray-500">
        Don't worry, you can explore all features at any time!
      </div>
    </div>
  );
}
